import { getLogger } from '../diagnostics/logger';
import moment from 'moment';
import {
  CANCEL_SIM_SUBMISSION_SUCCESS,
  CLEAR_SELECTED_ACCOUNT,
  CLEAR_SELECTED_SIM_SUBMISSION,
  COMPLETE_SIM_SUBMISSION_SUCCESS,
  LOAD_FILTER_SETTINGS,
  LOAD_FILTER_SETTINGS_FAILURE,
  LOAD_FILTER_SETTINGS_SUCCESS,
  LOAD_SELECTED_ACCOUNT,
  LOAD_SELECTED_ACCOUNT_FAILURE,
  LOAD_SELECTED_ACCOUNT_SUCCESS,
  LOAD_SELECTED_COMPANY,
  LOAD_SELECTED_COMPANY_FAILURE,
  LOAD_SELECTED_COMPANY_SUCCESS,
  LOAD_SELECTED_SIM_SUBMISSION,
  LOAD_SELECTED_SIM_SUBMISSION_FAILURE,
  LOAD_SELECTED_SIM_SUBMISSION_SUCCESS,
  SAVE_FILTER_SETTINGS_SUCCESS,
} from '../actions/actionTypes';
import { Action, ActionMeta } from '../actions/commonActions';
import { AccountDetail } from '../models/account';
import { Company } from '../models/company';
import { SimManagementError } from '../models/Errors';
import { QueryResult } from '../models/queryResult';
import { initialAppState } from '../models/state/appState';
import {
  FilterSettingsState,
  getDefaultFilterAccount,
  getDefaultFilterCompany,
  getDefaultSimSubmissionState,
  SimSubmissionCacheTimeout,
  SpendCapFileCacheTimeout,
  UserDefaultFilter,
} from '../models/state/filterSettingsState';
import { getDefaultSubmissionState } from '../models/state/submissionDefaults';
import { SimSubmission } from '../models/transfer/input/SimSubmission';
const log = getLogger('simManagement.reducers.filterSettingsReducer');

/**
 * Reducer which deals with processing individual companies.
 * @param state Initial state.
 * @param action An action.
 */
export default function filterSettingsReducer(
  state: FilterSettingsState = initialAppState.filter,
  action: Action<any>,
): FilterSettingsState {
  switch (action.type) {
    case LOAD_FILTER_SETTINGS: {
      const newState: Partial<FilterSettingsState> = {
        isLoading: true,
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_FILTER_SETTINGS_SUCCESS: {
      const { payload } = action as Action<UserDefaultFilter>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { company, account } = payload;
      const loadingStatus = {
        isLoading: false,
        lastLoadedOn: new Date(),
      };

      const newState: Partial<FilterSettingsState> = {
        account: account ? Object.assign({}, account, loadingStatus) : getDefaultFilterAccount(),
        company: company ? Object.assign({}, company, loadingStatus) : getDefaultFilterCompany(),
        isLoading: false,
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_FILTER_SETTINGS_FAILURE: {
      const newState: Partial<FilterSettingsState> = {
        isLoading: false,
      };

      return Object.assign({}, state, newState);
    }
    case SAVE_FILTER_SETTINGS_SUCCESS: {
      const { payload } = action as Action<FilterSettingsState>;
      const newState: Partial<FilterSettingsState> = {
        ...payload,
      };

      return Object.assign({}, state, newState);
    }
    case CLEAR_SELECTED_ACCOUNT: {
      const newState: Partial<FilterSettingsState> = {
        account: getDefaultFilterAccount(),
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_ACCOUNT: {
      const newState: Partial<FilterSettingsState> = {
        account: Object.assign(getDefaultFilterAccount(), {
          isLoading: true,
        }),
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_ACCOUNT_SUCCESS: {
      const { payload } = action as Action<AccountDetail>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState: Partial<FilterSettingsState> = {
        account: {
          ...payload,
          isLoading: false,
          lastLoadedOn: new Date(),
        },
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_ACCOUNT_FAILURE: {
      const error = action.payload as SimManagementError;
      const newState: Partial<FilterSettingsState> = {
        account: Object.assign(getDefaultFilterAccount(), { error }),
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_COMPANY: {
      const newState: Partial<FilterSettingsState> = {
        company: Object.assign(getDefaultFilterCompany(), { isLoading: true }),
        account: getDefaultFilterAccount(),
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_COMPANY_SUCCESS: {
      const company = action.payload as Company;
      const newState: Partial<FilterSettingsState> = {
        company: {
          ...company,
          isLoading: false,
          lastLoadedOn: new Date(),
        },
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_COMPANY_FAILURE: {
      const error = action.payload as SimManagementError;
      const newState: Partial<FilterSettingsState> = {
        company: Object.assign(getDefaultFilterCompany(), { error }),
      };

      return Object.assign({}, state, newState);
    }
    // SIM SUBMISSION
    case CLEAR_SELECTED_SIM_SUBMISSION: {
      const newState: Partial<FilterSettingsState> = {
        simSubmission: getDefaultSimSubmissionState(),
      };

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_SIM_SUBMISSION: {
      const newState: Partial<FilterSettingsState> = {
        simSubmission: {
          ...getDefaultSimSubmissionState(),
          isLoading: true,
        },
      };

      return Object.assign({}, state, newState);
    }
    case CANCEL_SIM_SUBMISSION_SUCCESS:
    case COMPLETE_SIM_SUBMISSION_SUCCESS:
    case LOAD_SELECTED_SIM_SUBMISSION_SUCCESS: {
      const { payload } = action as Action<SimSubmission>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState: Partial<FilterSettingsState> = Object.assign({}, state, {
        simSubmission: {
          ...payload,
          isLoading: false,
          expiresOn: moment().add(SimSubmissionCacheTimeout, 'seconds').toDate(),
          lastLoadedOn: new Date(),
        },
      });

      return Object.assign({}, state, newState);
    }
    case LOAD_SELECTED_SIM_SUBMISSION_FAILURE: {
      const { meta, payload } = action as ActionMeta<SimManagementError, string>;
      if (!meta) {
        log.warn('No meta');
        return state;
      }

      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState: Partial<FilterSettingsState> = {
        simSubmission: {
          ...getDefaultSimSubmissionState(),
          ticketNumber: meta,
          error: payload,
        },
      };

      return Object.assign({}, state, newState);
    }
  }

  return state;
}
